import type { ScheduleItemInputs } from "@/lib/api/model";
import { cn } from "@/lib/utils";

import { COPY } from "../../lib/copy";
import { reasonLabel } from "../../lib/labels";
import { MetaPill } from "./meta-badge";

/**
 * `inputs` is codes and counts only (spec 04 §7.3); the pill needs both member counts to say
 * anything useful. Rows with no decision yet may omit `inputs` or send an empty object.
 *
 * `external_for_skip` is spec 06 §3.3's separate count, read by join-ladder rung 3 alone;
 * `external − external_for_skip` is how many attendees a verified domain reclassified as
 * colleagues. Absent on every pre-06 row, which is why it defaults to zero rather than to
 * `external` — a missing key must not claim a domain did something.
 */
export function inputCounts(
  inputs: ScheduleItemInputs | Record<string, unknown> | null | undefined,
): { members: number; external: number; internalByDomain: number } | null {
  if (!inputs) return null;
  const { members, external, external_for_skip: externalForSkip } = inputs;
  if (typeof members !== "number" || typeof external !== "number") return null;
  const internalByDomain = typeof externalForSkip === "number" ? Math.max(0, external - externalForSkip) : 0;
  return { members, external, internalByDomain };
}

/** Reason label + the counts pill — shared by the Schedule tab and the Meetings list (spec 04 §9 "Why skipped"). */
export function WhySkipped({
  reason,
  inputs,
  className,
}: {
  reason: string | null;
  inputs?: ScheduleItemInputs | Record<string, unknown> | null;
  className?: string;
}) {
  const counts = inputCounts(inputs);
  if (!reason && !counts) return <span className="text-sm text-contrast/30">—</span>;
  return (
    <div className={cn("flex min-w-0 flex-wrap items-center gap-1.5", className)}>
      {reason && <span className="min-w-0 truncate text-sm text-contrast/70">{reasonLabel(reason)}</span>}
      {counts && <MetaPill icon="IconUser">{COPY.schedule.counts(counts.members, counts.external)}</MetaPill>}
      {counts && counts.internalByDomain > 0 && (
        <MetaPill icon="IconGlobe">{COPY.schedule.onVerifiedDomain(counts.internalByDomain)}</MetaPill>
      )}
    </div>
  );
}

/** Every non-processing terminal row can explain itself (spec §4.5 names `empty` / `blocked`; `cancelled(admin|…)`, `failed(*)`, `discarded(too_short)`, `no_show` are the others). */
export const WHY_STATES = ["skipped", "declined", "blocked", "cancelled", "failed", "empty", "discarded", "no_show"] as const;
export function showsWhy(state: string): boolean {
  return (WHY_STATES as readonly string[]).includes(state);
}

/** Attendee mix explains skip / decline / block. Terminal bot outcomes already have a reason label — a counts pill there is noise. */
export const COUNT_STATES = ["skipped", "declined", "blocked"] as const;
export function showsCounts(state: string): boolean {
  return (COUNT_STATES as readonly string[]).includes(state);
}
